import React, { useState, useRef } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Textarea } from './ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Image, Video, X, Lock, Users, Globe, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';

const PostComposer = ({ collapsed = false, onPostCreated = () => {} }) => {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreviews, setMediaPreviews] = useState([]);
  const [privacy, setPrivacy] = useState('followers');
  const [commentsEnabled, setCommentsEnabled] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [expanded, setExpanded] = useState(!collapsed);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // Get creator profile for avatar
  const [creatorProfile, setCreatorProfile] = useState(null);
  React.useEffect(() => {
    if (user) {
      axios.get(`${API}/creator/profile/${user.handle || user.id}`)
        .then(res => setCreatorProfile(res.data))
        .catch(err => console.error('Error loading profile:', err));
    }
  }, [user]);

  const handleMediaSelect = async (files, type) => {
    if (mediaFiles.length + files.length > 4) {
      toast.error('Maximum 4 media files allowed');
      return;
    }

    const validFiles = [];
    const validPreviews = [];

    for (const file of files) {
      // Validate file size
      const maxSize = type === 'video' ? 100 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`File too large. Max size: ${type === 'video' ? '100MB' : '5MB'}`);
        continue;
      }

      validFiles.push({ file, type });

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        validPreviews.push({
          url: e.target.result,
          type,
          name: file.name
        });
        if (validPreviews.length === validFiles.length) {
          setMediaPreviews([...mediaPreviews, ...validPreviews]);
        }
      };
      reader.readAsDataURL(file);
    }

    setMediaFiles([...mediaFiles, ...validFiles]);
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    handleMediaSelect(files, 'image');
  };

  const handleVideoSelect = (e) => {
    const files = Array.from(e.target.files);
    handleMediaSelect(files, 'video');
  };

  const removeMedia = (index) => {
    setMediaFiles(mediaFiles.filter((_, i) => i !== index));
    setMediaPreviews(mediaPreviews.filter((_, i) => i !== index));
  };

  const uploadMedia = async () => {
    const uploadedUrls = [];
    setUploading(true);

    try {
      for (const mediaFile of mediaFiles) {
        const formData = new FormData();
        formData.append('file', mediaFile.file);

        const token = localStorage.getItem('token');
        const response = await axios.post(`${API}/upload/creator-post-media`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });

        uploadedUrls.push(response.data.url);
      }

      return uploadedUrls;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload media');
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handlePost = async () => {
    if (!content.trim() && mediaFiles.length === 0) {
      toast.error('Please add some content or media');
      return;
    }

    if (content.length > 2000) {
      toast.error('Content must be 2000 characters or less');
      return;
    }

    setPosting(true);

    try {
      // Upload media first
      let mediaUrls = [];
      if (mediaFiles.length > 0) {
        mediaUrls = await uploadMedia();
      }

      // Determine post type
      let postType = 'text';
      if (mediaFiles.length > 0) {
        const hasVideo = mediaFiles.some(m => m.type === 'video');
        postType = hasVideo ? 'video' : 'image';
        if (mediaFiles.length > 1) postType = 'mixed';
      }

      // Create post
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/creator/posts`,
        {
          content: content.trim(),
          media_urls: mediaUrls,
          post_type: postType,
          visibility: privacy,  // Changed from 'privacy' to 'visibility'
          comments_enabled: commentsEnabled,
          status: 'published'  // Explicitly set status to published
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      toast.success('Post created!');
      
      // Reset form
      setContent('');
      setMediaFiles([]);
      setMediaPreviews([]);
      setPrivacy('followers');
      setCommentsEnabled(true);
      
      // Callback with new post
      onPostCreated(response.data);

      // Collapse if was collapsed initially
      if (collapsed) {
        setExpanded(false);
      }

    } catch (error) {
      console.error('Post error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create post');
    } finally {
      setPosting(false);
    }
  };

  if (!expanded && collapsed) {
    return (
      <Card 
        className="bg-gray-900 border-gray-800 cursor-pointer hover:border-gray-700 transition-all"
        onClick={() => setExpanded(true)}
      >
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Avatar className="w-10 h-10">
              <AvatarImage src={creatorProfile?.avatar_url} />
              <AvatarFallback>{user?.email?.[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="flex-1 bg-gray-800 rounded-full px-4 py-2 text-gray-400">
              What's on your mind?
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gray-900 border-gray-800">
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-start gap-3">
          <Avatar className="w-10 h-10">
            <AvatarImage src={creatorProfile?.avatar_url} />
            <AvatarFallback>{user?.email?.[0]?.toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="What's on your mind?"
              className="bg-gray-800 border-gray-700 text-white resize-none min-h-[80px]"
              maxLength={2000}
            />
            <div className="text-xs text-gray-500 mt-1">
              {content.length}/2000
            </div>
          </div>
        </div>

        {/* Media Previews */}
        {mediaPreviews.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {mediaPreviews.map((preview, index) => (
              <div key={index} className="relative rounded-lg overflow-hidden bg-gray-800 aspect-square">
                {preview.type === 'image' ? (
                  <img src={preview.url} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="relative w-full h-full">
                    <video src={preview.url} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <Video className="w-12 h-12 text-white" />
                    </div>
                  </div>
                )}
                <Button
                  size="icon"
                  variant="ghost"
                  className="absolute top-2 right-2 bg-black/50 hover:bg-black/75"
                  onClick={() => removeMedia(index)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-800">
          <div className="flex items-center gap-2">
            {/* Image Upload */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={handleImageSelect}
              className="sr-only"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={mediaFiles.length >= 4 || uploading || posting}
              className="text-gray-400 hover:text-white"
            >
              <Image className="w-4 h-4 mr-2" />
              Photo
            </Button>

            {/* Video Upload */}
            <input
              ref={videoInputRef}
              type="file"
              accept="video/mp4,video/webm"
              onChange={handleVideoSelect}
              className="sr-only"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => videoInputRef.current?.click()}
              disabled={mediaFiles.length >= 4 || uploading || posting}
              className="text-gray-400 hover:text-white"
            >
              <Video className="w-4 h-4 mr-2" />
              Video
            </Button>

            {/* Privacy Selector */}
            <select
              value={privacy}
              onChange={(e) => setPrivacy(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white"
              disabled={uploading || posting}
            >
              <option value="public">🌍 Public</option>
              <option value="followers">👥 Followers</option>
              <option value="subscribers">💎 Subscribers</option>
            </select>
          </div>

          {/* Post Button */}
          <Button
            onClick={handlePost}
            disabled={(!content.trim() && mediaFiles.length === 0) || uploading || posting}
            className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
            ) : posting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Posting...</>
            ) : (
              'Post'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PostComposer;
